import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { setAuth } from '../../lib/auth';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

/**
 * OAuth callback landing.
 * Backend redirects to /oauth/callback?token=<JWT>&provider=<google|microsoft>.
 * We fetch /auth/me + /auth/accounts to build the same setAuth() payload the
 * standard login flow uses, then push into /welcome.
 */
export default function OAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = searchParams.get('token');
    const provider = searchParams.get('provider') || 'sso';

    if (!token) {
      const err = searchParams.get('error') || 'missing_token';
      setError(err);
      toast.error('Sign-in failed. Please try again.');
      setTimeout(() => navigate('/signup?error=' + err), 1500);
      return;
    }

    (async () => {
      try {
        // Stash token so api client uses it for the follow-up call
        localStorage.setItem('token', token);
        const { data: userResp } = await api.get('/auth/me');
        const { data: accResp } = await api.get('/auth/accounts');
        const accounts = Array.isArray(accResp?.accounts) ? accResp.accounts : (Array.isArray(accResp) ? accResp : []);
        setAuth(token, userResp, accounts);
        toast.success(`Signed in with ${provider}.`);
        navigate('/welcome');
      } catch (e) {
        setError('profile_fetch_failed');
        toast.error('Signed in, but could not load your profile.');
        setTimeout(() => navigate('/signup?error=oauth_profile'), 1500);
      }
    })();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center" data-testid="oauth-callback-page">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary mb-4"/>
        <p className="text-sm text-slate-500">
          {error ? 'Something went wrong. Redirecting…' : 'Signing you in…'}
        </p>
      </div>
    </div>
  );
}
